import Workspace, { WorkspaceDocument } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import { findFolderById } from "@/pages/workspace/utils/folderUtils";
import { formatDateToDanishForFilename } from "@/utils/format";
import { useServices } from "@/hooks/useServices";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import JSZip from "jszip";

interface UseFilesTabFileOperationsProps {
  workspace: Workspace | null;
  setIsSyncingToSharePoint: (syncing: boolean) => void;
  setSyncDocument: (doc: WorkspaceDocument | null) => void;
}

interface UseFilesTabFileOperationsReturn {
  handleDownloads: (
    e: React.MouseEvent,
    items: Item[],
    version?: number
  ) => Promise<void>;
  generateFilenameWithTimestamp: (item: Item, version?: number) => string;
  onSyncToSharePointClick: (item: Item) => void;
}

/**
 * Hook for handling file operations in FilesTab
 *
 * Manages:
 * - File downloads (single and batch)
 * - Filename generation with timestamps
 * - SharePoint sync initiation
 *
 * Downloads files from Azure Blob Storage and generates appropriate filenames
 * with timestamps for version tracking. Also handles initiating SharePoint sync
 * by opening the sync dialog.
 */
export const useFilesTabFileOperations = ({
  workspace,
  setIsSyncingToSharePoint,
  setSyncDocument,
}: UseFilesTabFileOperationsProps): UseFilesTabFileOperationsReturn => {
  const services = useServices();
  const blobStorageService = services.items.blobStorage;
  const workspaceService = services.items.workspace;

  const sanitizeZipPathSegment = (name: string) =>
    name.replaceAll("/", "_").replaceAll("\\", "_");

  const saveBlobAsFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadBlobAsFile = async (downloadUrl: string, filename: string) => {
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`
      );
    }
    const blob = await response.blob();
    saveBlobAsFile(blob, filename);
  };

  const downloadFolderAsZip = async (
    folderItem: Item,
    tokenedUrl: { uriBase: string; sasToken: string }
  ) => {
    if (!workspace) return;

    const folder = findFolderById(workspace, folderItem.id);
    if (!folder) return;

    const rootFolderName = sanitizeZipPathSegment(folderItem.name);
    const updatedAt = folderItem.updatedAt
      ? new Date(folderItem.updatedAt)
      : new Date();
    const zipFilename = `${rootFolderName}_${formatDateToDanishForFilename(updatedAt)}.zip`;

    const zip = new JSZip();

    const addFolderContents = async (
      currentFolderId: string,
      zipFolderPath: string
    ) => {
      const currentFolder = findFolderById(workspace, currentFolderId);
      if (!currentFolder) return;

      // Add files in this folder
      for (const doc of currentFolder.documents ?? []) {
        const downloadUrl = `${tokenedUrl.uriBase}${doc.blobName}${tokenedUrl.sasToken}`;
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          logger.error("Failed to download a file for the folder zip", {
            operation: "downloadFolderAsZip",
            workspaceId: workspace.id,
            documentId: doc.id,
            status: response.status,
            statusText: response.statusText,
          });
          continue;
        }

        const blob = await response.blob();
        const entryName = `${zipFolderPath}/${sanitizeZipPathSegment(doc.name)}`;
        zip.file(entryName, blob, { binary: true });
      }

      // Recurse subfolders
      for (const sub of currentFolder.folders ?? []) {
        const subName = sanitizeZipPathSegment(sub.name);
        await addFolderContents(sub.id, `${zipFolderPath}/${subName}`);
      }
    };

    await addFolderContents(folderItem.id, rootFolderName);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveBlobAsFile(zipBlob, zipFilename);
  };

  const generateFilenameWithTimestamp = (
    item: Item,
    version?: number
  ): string => {
    if (!item.updatedAt) {
      return item.name;
    }

    const updatedDate = new Date(
      item.revisions?.find((revision) => revision.revisionVersion === version)
        ?.updatedAt || item.updatedAt
    );
    const formattedDate = formatDateToDanishForFilename(updatedDate);

    const lastDotIndex = item.name.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${item.name}_${formattedDate}`;
    } else {
      const nameWithoutExt = item.name.substring(0, lastDotIndex);
      const extension = item.name.substring(lastDotIndex);
      return `${nameWithoutExt}_${formattedDate}${extension}`;
    }
  };

  const handleDownloads = async (
    e: React.MouseEvent,
    items: Item[],
    version?: number
  ) => {
    e.stopPropagation();

    try {
      // For folder downloads, we need a generic SAS token
      let tokenedUrl: { uriBase: string; sasToken: string } | null = null;

      for (const item of items) {
        if (item.type === "document" && workspace) {
          // Use tracked endpoint for document downloads
          const revisionVersion = version ?? item.activeRevision ?? 1;
          const revision = item.revisions?.find(
            (r) => r.revisionVersion === revisionVersion
          );
          const blobId = revision?.blobId ?? item.blobName;

          if (!blobId) continue;

          // Always use isIndexed=false for downloads to fetch the original file
          // from the 'documents' container. The 'workspace-documents' container
          // holds PDF-converted versions intended for the viewer, not for download.
          const urlResponse = await workspaceService.getDocumentDownloadUrl(
            workspace.id,
            item.id,
            blobId,
            revisionVersion,
            false
          );

          if (!urlResponse.success) {
            logger.error(
              "Failed to get a download URL for a workspace document",
              {
                operation: "downloadWorkspaceItems",
                workspaceId: workspace.id,
                documentId: item.id,
                revisionVersion,
                status: urlResponse.status,
              }
            );
            showErrorToast(
              `Fejl ved hentning af download URL for ${item.name}`
            );
            continue;
          }

          await downloadBlobAsFile(
            urlResponse.value.url,
            generateFilenameWithTimestamp(item, version)
          );
          continue;
        }

        // Folder download uses generic SAS token (not tracked individually)
        if (!tokenedUrl) {
          const tokenResult = await blobStorageService.getTokenedUrl();
          if (!tokenResult.success) {
            logger.error("Failed to get a SAS URL for a folder download", {
              operation: "downloadWorkspaceItems",
              workspaceId: workspace?.id,
              folderId: item.id,
              status: tokenResult.status,
            });
            showErrorToast("Fejl ved hentning af download URL");
            return;
          }
          tokenedUrl = tokenResult.value;
        }
        await downloadFolderAsZip(item, tokenedUrl);
      }
    } catch (error) {
      logger.error("Failed to download workspace items", {
        operation: "downloadWorkspaceItems",
        error,
        workspaceId: workspace?.id,
        itemCount: items.length,
      });
      showErrorToast("Fejl ved download af fil");
    }
  };

  const onSyncToSharePointClick = (item: Item) => {
    setIsSyncingToSharePoint(true);
    setSyncDocument(item as WorkspaceDocument);
  };

  return {
    handleDownloads,
    generateFilenameWithTimestamp,
    onSyncToSharePointClick,
  };
};
